'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// NODE_WIDTH: fixed width per node cell (px)
const NODE_WIDTH = 140;
// NODE_HEIGHT: fallback/minimum height of a node card (px)
const NODE_HEIGHT = 140;
// HORIZONTAL_GAP: minimum gap between sibling nodes (px)
const HORIZONTAL_GAP = 20;
// TOP_LINE_HEIGHT: fixed height of vertical line above each child slot (px)
const TOP_LINE_HEIGHT = 30;
// BOTTOM_LINE_HEIGHT: fixed height of vertical line below each parent slot (px)
const BOTTOM_LINE_HEIGHT = 30;

// COUNTER_MARGIN: extra space reserved on each side of the canvas for the left/right downline counters
const COUNTER_WIDTH = 100;
const COUNTER_MARGIN = COUNTER_WIDTH + 20; // counter width + gap

interface NetworkAccount {
  id: number;
  user_id: string | null;
  user_number: number;
  name: string | null;
  username: string | null;
  email: string | null;
  binary_id: number | null;
  binary_level: number | null;
  binary_position: 'L' | 'R' | null;
  binary_status: string;
  package_type: string | null;
  sponsor_id: number;
  sponsor_name: string | null;
  created_at?: string | null;
  avatar_url?: string | null;
}

type SlotMap = Map<number, NetworkAccount | null>;

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function formatUserNumber(num: number): string {
  return 'PH' + String(num).padStart(6, '0');
}

function slotPosition(slot: number): 'L' | 'R' {
  return slot % 2 === 0 ? 'L' : 'R';
}

// Count all occupied descendants of a given slot within the slotMap (up to slot 15)
function countDescendants(slot: number, slots: SlotMap): number {
  let count = 0;
  const queue = [slot * 2, slot * 2 + 1];
  while (queue.length > 0) {
    let s = queue.shift()!;
    if (s > 15) continue;
    if (slots.get(s)) {
      count++;
      queue.push(s * 2, s * 2 + 1);
    }
  }
  return count;
}

// Count all occupied slots in the left subtree (slots 2,4,5,8,9,10,11)
function countLeftDownline(slots: SlotMap): number {
  const leftSubtree = [2, 4, 5, 8, 9, 10, 11];
  return leftSubtree.filter(s => slots.get(s) != null).length;
}

// Count all occupied slots in the right subtree (slots 3,6,7,12,13,14,15)
function countRightDownline(slots: SlotMap): number {
  const rightSubtree = [3, 6, 7, 12, 13, 14, 15];
  return rightSubtree.filter(s => slots.get(s) != null).length;
}

interface NodeCardProps {
  account: NetworkAccount;
  slot: number;
  isRoot?: boolean;
  onClick?: () => void;
  onHeightChange?: (slot: number, height: number) => void;
}

function NodeCard({ account, slot, isRoot = false, onClick, onHeightChange }: NodeCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const initials = getInitials(account.name);
  const userNum = formatUserNumber(account.user_number);
  const isActive = account.binary_status === 'Active';

  const avatarSize = isRoot ? 'w-16 h-16 text-lg' : 'w-12 h-12 text-sm';
  const avatarBgColor = isRoot ? 'var(--primary)' : 'rgba(100,116,139,0.6)';

  useEffect(() => {
    if (!ref.current || !onHeightChange) return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onHeightChange(slot, entry.contentRect.height + 24); // +24 for padding (p-3 = 12px top+bottom)
      }
    });
    ro.observe(el);
    // Initial measurement
    onHeightChange(slot, el.offsetHeight);
    return () => ro.disconnect();
  }, [slot, onHeightChange]);

  return (
    <div
      ref={ref}
      className={`flex flex-col items-center border border-border rounded-xl p-3 bg-card ${!isRoot && onClick ? 'cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors' : ''}`}
      style={{ minWidth: NODE_WIDTH - 8, minHeight: NODE_HEIGHT }}
      onClick={!isRoot && onClick ? onClick : undefined}
      title={!isRoot && onClick ? `Click to view ${account.name}'s downlines` : undefined}
    >
      {/* Avatar circle */}
      <div
        className={`flex items-center justify-center rounded-full overflow-hidden font-bold text-white mb-2 flex-shrink-0 ${avatarSize}`}
        style={{ backgroundColor: account.avatar_url ? undefined : avatarBgColor }}
      >
        {account.avatar_url ? (
          <img
            src={account.avatar_url}
            alt={account.name || 'Avatar'}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.style.backgroundColor = avatarBgColor;
                parent.textContent = initials;
              }
            }}
          />
        ) : (
          initials
        )}
      </div>

      {/* Info */}
      <p className={`font-semibold text-foreground text-center leading-tight ${isRoot ? 'text-sm' : 'text-xs'}`}>
        {account.name || 'Unknown'}
      </p>
      <p className={`text-muted-foreground text-center mt-0.5 ${isRoot ? 'text-xs' : 'text-[10px]'}`}>
        {userNum}
      </p>
      {account.username && (
        <p className={`text-muted-foreground text-center mt-0.5 ${isRoot ? 'text-[10px]' : 'text-[9px]'}`}>
          {account.username}
        </p>
      )}
      {account.package_type && (
        <span className="mt-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
          {account.package_type}
        </span>
      )}
      <span
        className={`mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
          isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {isActive ? 'Active' : 'Inactive'}
      </span>
    </div>
  );
}

function EmptySlot({ isRoot = false, slot, onHeightChange }: { isRoot?: boolean; slot?: number; onHeightChange?: (slot: number, height: number) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const size = isRoot ? 'w-16 h-16' : 'w-12 h-12';

  useEffect(() => {
    if (!ref.current || !onHeightChange || slot === undefined) return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onHeightChange(slot, entry.contentRect.height + 24);
      }
    });
    ro.observe(el);
    onHeightChange(slot, el.offsetHeight);
    return () => ro.disconnect();
  }, [slot, onHeightChange]);

  return (
    <div ref={ref} className="flex flex-col items-center border border-dashed border-border rounded-xl p-3 bg-muted/10" style={{ minWidth: NODE_WIDTH - 8, minHeight: NODE_HEIGHT }}>
      <div className={`flex items-center justify-center ${size} rounded-full border-2 border-dashed border-border bg-muted/30 mb-2`}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-muted-foreground" />
        </svg>
      </div>
      <p className="text-muted-foreground text-[10px]">Empty</p>
    </div>
  );
}

interface FlatTreeProps {
  slots: SlotMap;
  maxSlot: number;
  leftDownline: number;
  rightDownline: number;
  onSlotClick: (account: NetworkAccount) => void;
  leafDownlineCounts: Map<number, number>;
}

// Compute the x-center position for each slot using a bottom-up approach.
function computePositions(maxSlot: number): Map<number, number> {
  const pos = new Map<number, number>();
  const numLevels = Math.floor(Math.log2(maxSlot + 1));

  const leafStart = Math.pow(2, numLevels - 1);
  const leafEnd = Math.min(Math.pow(2, numLevels) - 1, maxSlot);
  const leafCount = leafEnd - leafStart + 1;

  for (let i = 0; i < leafCount; i++) {
    let slot = leafStart + i;
    pos.set(slot, i * (NODE_WIDTH + HORIZONTAL_GAP) + NODE_WIDTH / 2);
  }

  for (let lvl = numLevels - 2; lvl >= 0; lvl--) {
    const start = Math.pow(2, lvl);
    const end = Math.min(Math.pow(2, lvl + 1) - 1, maxSlot);
    for (let slot = start; slot <= end; slot++) {
      const leftChild = slot * 2;
      const rightChild = slot * 2 + 1;
      const leftPos = pos.get(leftChild);
      const rightPos = pos.get(rightChild);
      if (leftPos !== undefined && rightPos !== undefined) {
        pos.set(slot, (leftPos + rightPos) / 2);
      } else if (leftPos !== undefined) {
        pos.set(slot, leftPos);
      } else if (rightPos !== undefined) {
        pos.set(slot, rightPos);
      } else {
        const parentSlot = Math.floor(slot / 2);
        const parentPos = pos.get(parentSlot);
        if (parentPos !== undefined) {
          pos.set(slot, slot % 2 === 0 ? parentPos - (NODE_WIDTH + HORIZONTAL_GAP) / 2 : parentPos + (NODE_WIDTH + HORIZONTAL_GAP) / 2);
        } else {
          pos.set(slot, 0);
        }
      }
    }
  }

  return pos;
}

function FlatTree({ slots, maxSlot, leftDownline, rightDownline, onSlotClick, leafDownlineCounts }: FlatTreeProps) {
  const numLevels = Math.floor(Math.log2(maxSlot + 1));
  const positions = computePositions(maxSlot);

  // Track measured heights per slot
  const [slotHeights, setSlotHeights] = useState<Map<number, number>>(new Map());

  const handleHeightChange = useCallback((slot: number, height: number) => {
    setSlotHeights(prev => {
      const current = prev.get(slot);
      if (current === height) return prev;
      const next = new Map(prev);
      next.set(slot, height);
      return next;
    });
  }, []);

  // Build levels array
  const levels: number[][] = [];
  for (let lvl = 0; lvl < numLevels; lvl++) {
    const start = Math.pow(2, lvl);
    const end = Math.min(Math.pow(2, lvl + 1) - 1, maxSlot);
    const row: number[] = [];
    for (let s = start; s <= end; s++) row.push(s);
    levels.push(row);
  }

  // Compute the max height of each level row (using measured heights or fallback)
  const rowHeights: number[] = levels.map((row) => {
    let maxH = NODE_HEIGHT;
    for (let slot of row) {
      const h = slotHeights.get(slot);
      if (h !== undefined && h > maxH) maxH = h;
    }
    return maxH;
  });

  // Compute cumulative Y top positions for each level
  // levelY[i] = top Y of level i's nodes
  const levelY: number[] = [];
  let cumY = 0;
  for (let i = 0; i < numLevels; i++) {
    levelY.push(cumY);
    // After this level: rowHeight + bottom line + top line of next level
    cumY += rowHeights[i] + BOTTOM_LINE_HEIGHT + TOP_LINE_HEIGHT;
  }

  // Find total canvas width — add COUNTER_MARGIN on both sides for the left/right counters
  let minX = Infinity, maxX = -Infinity;
  for (let s = 1; s <= maxSlot; s++) {
    const x = positions.get(s);
    if (x !== undefined) {
      minX = Math.min(minX, x - NODE_WIDTH / 2);
      maxX = Math.max(maxX, x + NODE_WIDTH / 2);
    }
  }
  // offsetX shifts all nodes right by COUNTER_MARGIN so the left counter has room
  const offsetX = -minX + 20 + COUNTER_MARGIN;
  const canvasWidth = maxX - minX + 40 + COUNTER_MARGIN * 2;

  const leafLevelIdx = levels.length - 1;
  // Extra height for leaf downline counters
  const canvasHeight = cumY + 60;

  return (
    <div style={{ position: 'relative', width: canvasWidth, height: canvasHeight }}>
      {/* SVG connector lines */}
      <svg
        width={canvasWidth}
        height={canvasHeight}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
      >
        {levels.map((row, lvlIdx) => {
          if (lvlIdx === 0) return null;
          return row.map((slot) => {
            const parentSlot = Math.floor(slot / 2);
            const childX = (positions.get(slot) ?? 0) + offsetX;
            const parentX = (positions.get(parentSlot) ?? 0) + offsetX;

            const parentRowH = rowHeights[lvlIdx - 1];
            const parentNodeTop = levelY[lvlIdx - 1];
            const childNodeTop = levelY[lvlIdx];

            // Bottom of parent node card (using actual row height)
            const parentBottom = parentNodeTop + parentRowH;
            // midY: horizontal connector runs at parentBottom + BOTTOM_LINE_HEIGHT
            const midY = parentBottom + BOTTOM_LINE_HEIGHT;

            return (
              <g key={slot}>
                {/* Fixed-height vertical line below parent */}
                <line
                  x1={parentX} y1={parentBottom}
                  x2={parentX} y2={midY}
                  stroke="var(--border)" strokeWidth="1.5"
                />
                {/* Horizontal to child column */}
                <line
                  x1={parentX} y1={midY}
                  x2={childX} y2={midY}
                  stroke="var(--border)" strokeWidth="1.5"
                />
                {/* Fixed-height vertical line above child slot */}
                <line
                  x1={childX} y1={midY}
                  x2={childX} y2={childNodeTop}
                  stroke="var(--border)" strokeWidth="1.5"
                />
              </g>
            );
          });
        })}
      </svg>

      {/* Root level: flanked by left/right downline counters */}
      {(() => {
        const rootX = (positions.get(1) ?? 0) + offsetX;
        const rootY = levelY[0];
        const account = slots.get(1);
        return (
          <>
            {/* Left downline counter */}
            <div
              style={{
                position: 'absolute',
                left: rootX - NODE_WIDTH / 2 - COUNTER_MARGIN,
                top: rootY + rowHeights[0] / 2 - 30,
                width: COUNTER_WIDTH,
                textAlign: 'center',
              }}
            >
              <span className="text-3xl font-bold text-foreground">{leftDownline}</span>
              <p className="text-xs text-muted-foreground mt-1">Left</p>
            </div>

            {/* Root node */}
            <div
              style={{
                position: 'absolute',
                left: rootX - NODE_WIDTH / 2,
                top: rootY,
                width: NODE_WIDTH,
              }}
              className="flex flex-col items-center"
            >
              {account
                ? <NodeCard account={account} slot={1} isRoot onHeightChange={handleHeightChange} />
                : <EmptySlot isRoot slot={1} onHeightChange={handleHeightChange} />
              }
            </div>

            {/* Right downline counter */}
            <div
              style={{
                position: 'absolute',
                left: rootX + NODE_WIDTH / 2 + 20,
                top: rootY + rowHeights[0] / 2 - 30,
                width: COUNTER_WIDTH,
                textAlign: 'center',
              }}
            >
              <span className="text-3xl font-bold text-foreground">{rightDownline}</span>
              <p className="text-xs text-muted-foreground mt-1">Right</p>
            </div>
          </>
        );
      })()}

      {/* Non-root nodes */}
      {levels.slice(1).map((row, lvlOffset) => {
        const lvlIdx = lvlOffset + 1;
        const isLeafLevel = lvlIdx === leafLevelIdx;
        return row.map((slot) => {
          const x = (positions.get(slot) ?? 0) + offsetX;
          const y = levelY[lvlIdx];
          const account = slots.get(slot);
          return (
            <div
              key={slot}
              style={{
                position: 'absolute',
                left: x - NODE_WIDTH / 2,
                top: y,
                width: NODE_WIDTH,
              }}
              className="flex flex-col items-center"
            >
              {account
                ? <NodeCard account={account} slot={slot} onClick={() => onSlotClick(account)} onHeightChange={handleHeightChange} />
                : <EmptySlot slot={slot} onHeightChange={handleHeightChange} />
              }

              {/* Downline counter below leaf nodes */}
              {isLeafLevel && (
                <div className="flex flex-col items-center mt-2">
                  <svg width="16" height="12" viewBox="0 0 16 12" className="text-muted-foreground mb-0.5">
                    <path d="M2 2l6 8 6-8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-sm font-bold text-foreground">
                    {account ? (leafDownlineCounts.get(account.id) ?? 0) : 0}
                  </span>
                </div>
              )}
            </div>
          );
        });
      })}
    </div>
  );
}

function maxSlotForLevel(level: number): number {
  return Math.pow(2, level) - 1;
}

export default function BinaryGenealogyTree() {
  const { user } = useAuth();
  const [slots, setSlots] = useState<SlotMap>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayLevel, setDisplayLevel] = useState(4);
  const [stats, setStats] = useState({ total: 0, active: 0, left: 0, right: 0 });
  const [rootAccountId, setRootAccountId] = useState<number | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<{ id: number | null; name: string }[]>([]);
  const [myAccountId, setMyAccountId] = useState<number | null>(null);
  const [leafDownlineCounts, setLeafDownlineCounts] = useState<Map<number, number>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();

  const fetchGenealogyData = useCallback(async (rootId: number | null) => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);

      let rootAccount: NetworkAccount | null = null;

      if (rootId === null) {
        const { data, error: myErr } = await supabase
          .from('richesse_network_accounts')
          .select('id, user_id, user_number, name, username, email, binary_id, binary_level, binary_position, binary_status, package_type, sponsor_id, sponsor_name, created_at')
          .eq('user_id', user.id)
          .maybeSingle();
        if (myErr) { setError('Failed to load your network account.'); return; }
        if (!data) { setError('No network account found for your profile.'); return; }
        rootAccount = data as NetworkAccount;
      } else {
        const { data, error: accErr } = await supabase
          .from('richesse_network_accounts')
          .select('id, user_id, user_number, name, username, email, binary_id, binary_level, binary_position, binary_status, package_type, sponsor_id, sponsor_name, created_at')
          .eq('id', rootId)
          .maybeSingle();
        if (accErr) { setError('Failed to load account.'); return; }
        if (!data) { setError('Account not found.'); return; }
        rootAccount = data as NetworkAccount;
      }

      const slotMap: SlotMap = new Map();
      const accountIdToSlot = new Map<number, number>();

      slotMap.set(1, rootAccount);
      accountIdToSlot.set(rootAccount.id, 1);

      const MAX_SLOT = 15;
      let currentSlots: number[] = [1];

      for (let level = 1; level <= 4; level++) {
        const nextSlots: number[] = [];
        const occupiedSlotIds: { slot: number; accountId: number }[] = [];

        for (let slot of currentSlots) {
          const account = slotMap.get(slot);
          if (account) {
            occupiedSlotIds.push({ slot, accountId: account.id });
          } else {
            const leftChild = slot * 2;
            const rightChild = slot * 2 + 1;
            if (leftChild <= MAX_SLOT) {
              slotMap.set(leftChild, null);
              slotMap.set(rightChild, null);
            }
          }
        }

        if (occupiedSlotIds.length === 0) {
          for (let slot of currentSlots) {
            const leftChild = slot * 2;
            const rightChild = slot * 2 + 1;
            if (leftChild <= MAX_SLOT) {
              if (!slotMap.has(leftChild)) slotMap.set(leftChild, null);
              if (!slotMap.has(rightChild)) slotMap.set(rightChild, null);
              nextSlots.push(leftChild, rightChild);
            }
          }
          currentSlots = nextSlots;
          continue;
        }

        const accountIds = occupiedSlotIds.map(o => o.accountId);

        // Fetch direct children from richesse_network_accounts using binary_id and binary_position
        const { data: childAccounts, error: childErr } = await supabase
          .from('richesse_network_accounts')
          .select('id, user_id, user_number, name, username, email, binary_id, binary_level, binary_position, binary_status, package_type, sponsor_id, sponsor_name, created_at')
          .in('binary_id', accountIds);

        if (childErr) console.error('Error fetching child accounts at level', level, childErr);

        // Build a lookup: parentId -> { L: childAccount | null, R: childAccount | null }
        const childrenLookup = new Map<number, { L: NetworkAccount | null; R: NetworkAccount | null }>();
        if (childAccounts) {
          for (const child of childAccounts) {
            if (child.binary_id == null) continue;
            if (!childrenLookup.has(child.binary_id)) {
              childrenLookup.set(child.binary_id, { L: null, R: null });
            }
            const ch = childrenLookup.get(child.binary_id)!;
            if (child.binary_position === 'L') ch.L = child as NetworkAccount;
            else if (child.binary_position === 'R') ch.R = child as NetworkAccount;
          }
        }

        for (const { slot, accountId } of occupiedSlotIds) {
          const leftChildSlot = slot * 2;
          const rightChildSlot = slot * 2 + 1;
          if (leftChildSlot > MAX_SLOT) continue;

          const children = childrenLookup.get(accountId);
          const leftChild = children?.L ?? null;
          const rightChild = children?.R ?? null;

          if (leftChild !== null) {
            slotMap.set(leftChildSlot, leftChild);
            accountIdToSlot.set(leftChild.id, leftChildSlot);
          } else {
            slotMap.set(leftChildSlot, null);
          }

          if (rightChild !== null) {
            slotMap.set(rightChildSlot, rightChild);
            accountIdToSlot.set(rightChild.id, rightChildSlot);
          } else {
            slotMap.set(rightChildSlot, null);
          }

          nextSlots.push(leftChildSlot, rightChildSlot);
        }

        for (let slot of nextSlots) {
          if (!slotMap.has(slot)) slotMap.set(slot, null);
        }

        currentSlots = nextSlots;
      }

      for (let s = 1; s <= MAX_SLOT; s++) {
        if (!slotMap.has(s)) slotMap.set(s, null);
      }

      // Enrich with avatar_url
      const userIds: string[] = [];
      for (let s = 1; s <= MAX_SLOT; s++) {
        const acc = slotMap.get(s);
        if (acc && acc.user_id) userIds.push(acc.user_id);
      }
      if (userIds.length > 0) {
        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('id, avatar_url')
          .in('id', userIds);
        if (profileData) {
          const avatarMap = new Map<string, string | null>();
          for (const p of profileData) avatarMap.set(p.id, p.avatar_url ?? null);
          for (let s = 1; s <= MAX_SLOT; s++) {
            const acc = slotMap.get(s);
            if (acc && acc.user_id) {
              slotMap.set(s, { ...acc, avatar_url: avatarMap.get(acc.user_id) ?? null });
            }
          }
        }
      }

      setSlots(slotMap);

      // Fetch actual total downline counts for all visible accounts from the DB
      const allAccountIds: number[] = [];
      for (let s = 1; s <= MAX_SLOT; s++) {
        const acc = slotMap.get(s);
        if (acc) allAccountIds.push(acc.id);
      }

      const newLeafDownlineCounts = new Map<number, number>();
      if (allAccountIds.length > 0) {
        const { data: downlineCounts } = await supabase
          .from('richesse_network_accounts_binaries')
          .select('binary_id')
          .in('binary_id', allAccountIds);

        if (downlineCounts) {
          for (const row of downlineCounts) {
            if (row.binary_id == null) continue;
            newLeafDownlineCounts.set(row.binary_id, (newLeafDownlineCounts.get(row.binary_id) ?? 0) + 1);
          }
        }
      }
      setLeafDownlineCounts(newLeafDownlineCounts);

      // Compute left/right totals from the root account's actual binaries
      const rootAccountId_local = rootAccount.id;
      let dbLeft = 0, dbRight = 0;
      const { data: rootBinaries } = await supabase
        .from('richesse_network_accounts_binaries')
        .select('binary_position')
        .eq('binary_id', rootAccountId_local);

      if (rootBinaries) {
        for (const row of rootBinaries) {
          if (row.binary_position === 'L') dbLeft++;
          else if (row.binary_position === 'R') dbRight++;
        }
      }

      let total = 0, active = 0;
      for (let s = 2; s <= MAX_SLOT; s++) {
        const acc = slotMap.get(s);
        if (acc) {
          total++;
          if (acc.binary_status === 'Active') active++;
        }
      }
      setStats({ total, active, left: dbLeft, right: dbRight });

    } catch (err: any) {
      setError(err.message || 'Failed to load genealogy data.');
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchGenealogyData(rootAccountId);
  }, [user, rootAccountId, fetchGenealogyData]);

  // Fetch the logged-in user's own network account id once
  useEffect(() => {
    if (!user) return;
    supabase
      .from('richesse_network_accounts')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setMyAccountId(data.id);
      });
  }, [user, supabase]);

  const handleSlotClick = useCallback((account: NetworkAccount) => {
    const currentRoot = slots.get(1);
    setBreadcrumb(prev => [
      ...prev,
      { id: rootAccountId ?? myAccountId, name: currentRoot?.name || 'Root' }
    ]);
    setRootAccountId(account.id);
  }, [slots, rootAccountId, myAccountId]);

  const handleBreadcrumbClick = useCallback((index: number) => {
    const target = breadcrumb[index];
    setBreadcrumb(prev => prev.slice(0, index));
    setRootAccountId(target.id);
  }, [breadcrumb]);

  // Back to Top: return to the logged-in user's own tree
  const handleBackToTop = useCallback(() => {
    setBreadcrumb([]);
    setRootAccountId(null);
  }, []);

  // Go Up N levels: navigate N steps back in breadcrumb history
  const handleGoUp = useCallback((levels: number) => {
    const stepsBack = Math.min(levels, breadcrumb.length);
    if (stepsBack === 0) return;
    const targetIndex = breadcrumb.length - stepsBack;
    const target = breadcrumb[targetIndex];
    setBreadcrumb(prev => prev.slice(0, targetIndex));
    setRootAccountId(target.id);
  }, [breadcrumb]);

  // Whether we are currently viewing the logged-in user's own tree
  const isAtMyRoot = rootAccountId === null || rootAccountId === myAccountId;

  // Center the scroll position on the root node whenever the tree changes
  // MUST be before any conditional returns to satisfy Rules of Hooks
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    // Use requestAnimationFrame to ensure the DOM has fully painted before measuring
    const raf = requestAnimationFrame(() => {
      el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    });
    return () => cancelAnimationFrame(raf);
  }, [slots, displayLevel, rootAccountId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 min-h-[400px] bg-card rounded-xl border border-border">
        <div className="w-8 h-8 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
        <p className="text-muted-foreground text-sm">Loading your binary genealogy tree…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 min-h-[400px] bg-card rounded-xl border border-border">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  if (slots.size === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 min-h-[400px] bg-card rounded-xl border border-border">
        <p className="text-muted-foreground text-sm">No genealogy data available.</p>
      </div>
    );
  }

  const maxSlot = maxSlotForLevel(displayLevel);
  const leftDownline = countLeftDownline(slots);
  const rightDownline = countRightDownline(slots);

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Members', value: stats.total },
          { label: 'Active', value: stats.active },
          { label: 'Left Leg', value: stats.left },
          { label: 'Right Leg', value: stats.right },
        ].map((stat) => (
          <div key={stat.label} className="bg-card border border-border rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{stat.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Back to Top + Go Up navigation buttons */}
      {(!isAtMyRoot || breadcrumb.length > 0) && (
        <div className="flex items-center gap-2 flex-wrap">
          {!isAtMyRoot && (
            <button
              onClick={handleBackToTop}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              title="Return to your own tree"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12L12 3l9 9" />
                <path d="M12 3v18" />
              </svg>
              Go Back Top
            </button>
          )}
          {[1, 2, 3].map((n) => (
            breadcrumb.length >= n && (
              <button
                key={n}
                onClick={() => handleGoUp(n)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-muted text-foreground hover:bg-muted/70 border border-border transition-colors"
                title={`Go back ${n} step${n > 1 ? 's' : ''} in navigation history`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
                Go Back {n}
              </button>
            )
          ))}
        </div>
      )}

      {/* Breadcrumb navigation */}
      {breadcrumb.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {breadcrumb.map((crumb, idx) => (
            <React.Fragment key={idx}>
              <button
                onClick={() => handleBreadcrumbClick(idx)}
                className="text-xs text-primary hover:underline font-medium px-2 py-1 rounded hover:bg-primary/10 transition-colors"
              >
                {crumb.name}
              </button>
              <span className="text-muted-foreground text-xs">/</span>
            </React.Fragment>
          ))}
          <span className="text-xs text-muted-foreground px-2 py-1">
            {slots.get(1)?.name || 'Current'}
          </span>
        </div>
      )}

      {/* Level switcher */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Show levels:</span>
        {[2, 3, 4].map((lvl) => (
          <button
            key={lvl}
            onClick={() => setDisplayLevel(lvl)}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              displayLevel === lvl
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {lvl}
          </button>
        ))}
      </div>

      {/* Tree */}
      <div ref={scrollContainerRef} className="bg-card border border-border rounded-xl overflow-auto p-6">
        <div style={{ minWidth: 'max-content', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
          <FlatTree
            slots={slots}
            maxSlot={maxSlot}
            leftDownline={stats.left}
            rightDownline={stats.right}
            onSlotClick={handleSlotClick}
            leafDownlineCounts={leafDownlineCounts}
          />
        </div>
      </div>
    </div>
  );
}
